/* eslint-disable @typescript-eslint/no-explicit-any */
import type { DatePickerProps } from "antd";
import { DatePicker as AntdDatePicker } from "antd";
import { RangePickerProps } from "antd/lib/date-picker";
import { ForumDateFormatSource, useForumDateFormatSource, useForumStartDate } from "components/ForumDateFormatProvider";
import moment from "moment";
import React from "react";
import { getForumDateFormat, getForumDateTimeFormat, toWallClockMoment } from "utils/dateUtils";

import styles from "./styles.module.less";

type DatePickerForumFormatProps = {
  showTime?: boolean | Record<string, unknown>;
};

export type CustomDatePickerProps = DatePickerProps & {
  onChange?: (value: any) => void;
} & DatePickerForumFormatProps;

export type CustomRangePickerProps = RangePickerProps & DatePickerForumFormatProps;

const getDatePickerFormat = (
  format: DatePickerProps["format"] | RangePickerProps["format"],
  showTime: DatePickerForumFormatProps["showTime"],
  contextSource?: ForumDateFormatSource,
) => {
  return format ?? (showTime ? getForumDateTimeFormat(contextSource) : getForumDateFormat(contextSource));
};

const getForumDefaultPickerValue = (forumStartDate?: string) => {
  if (!forumStartDate || !moment(forumStartDate, moment.ISO_8601, true).isValid()) {
    return undefined;
  }

  const startDate = toWallClockMoment(forumStartDate);
  return startDate.isValid() ? startDate : undefined;
};

const DatePicker = ({
  value,
  format,
  style,
  onChange,
  showTime,
  defaultPickerValue,
  ...props
}: CustomDatePickerProps) => {
  const contextSource = useForumDateFormatSource();
  const forumStartDate = useForumStartDate();
  const pickerProps = { ...props, showTime };
  const resolvedDefaultPickerValue =
    defaultPickerValue ?? (!value ? getForumDefaultPickerValue(forumStartDate) : undefined);

  const handleChange = (newValue: any) => {
    onChange?.(newValue ? newValue.format() : newValue);
  };

  return (
    <AntdDatePicker
      value={value ? moment(value) : value}
      defaultPickerValue={resolvedDefaultPickerValue}
      format={getDatePickerFormat(format, showTime, contextSource)}
      onChange={handleChange}
      style={style}
      {...pickerProps}
    />
  );
};

const CustomRangePicker = ({
  value,
  format,
  style,
  showTime,
  defaultPickerValue,
  ...props
}: CustomRangePickerProps) => {
  const contextSource = useForumDateFormatSource();
  const forumStartDate = useForumStartDate();
  const pickerProps = { ...props, showTime };
  const forumDefaultPickerValue = getForumDefaultPickerValue(forumStartDate);
  const hasValue = value?.some(Boolean);
  const resolvedDefaultPickerValue =
    defaultPickerValue ??
    (!hasValue && forumDefaultPickerValue ? [forumDefaultPickerValue, forumDefaultPickerValue] : undefined);

  return (
    <AntdDatePicker.RangePicker
      value={value}
      defaultPickerValue={resolvedDefaultPickerValue}
      format={getDatePickerFormat(format, showTime, contextSource)}
      style={style}
      popupClassName={styles.picker}
      {...pickerProps}
    />
  );
};

DatePicker.RangePicker = CustomRangePicker;

export default DatePicker;
